/**
 * ForgotPassword — two-phase Cognito password recovery.
 *
 * Phase 1: user enters email → we call forgotPassword(), Cognito emails
 *          a one-time verification code to the account's verified email.
 * Phase 2: user enters code + new password → confirmPassword() resets
 *          the password and bounces them back to /login.
 *
 * Reachable with location state from Login:
 *   { email }                                — prefill
 *   { email, autoSend: true, reason }        — PasswordResetRequired: send
 *                                              the code immediately and land
 *                                              on phase 2.
 *
 * We intentionally don't auto-sign-in after reset — an explicit login
 * step after a password change is the safer default.
 */
import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import {
  describeAuthError,
  normalizeCode,
  normalizeEmail,
  type AuthErrorInfo,
} from '../lib/authErrors';
import { Button, Card, Field, Loading, TextInput } from '../components/ui';
import { AuthAlert, AuthErrorAlert, PasswordInput } from './authWidgets';
import './auth.css';

type Phase = 'request' | 'confirm';

interface LocationState {
  email?: string;
  autoSend?: boolean;
  reason?: 'resetRequired';
}

/** Seconds before "Send again" re-enables — stops accidental double sends tripping Cognito's limit. */
const RESEND_COOLDOWN_S = 30;

export default function ForgotPassword() {
  const { user, loading, requestPasswordReset, confirmPasswordReset } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = (location.state as LocationState | null) ?? null;

  const [phase, setPhase] = useState<Phase>('request');
  const [email, setEmail] = useState(locationState?.email ?? '');
  // The username form Cognito accepted in phase 1 — reused verbatim in
  // phase 2 so the two calls agree even on a case-sensitive pool.
  const [resolvedUsername, setResolvedUsername] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [error, setError] = useState<AuthErrorInfo | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const autoSent = useRef(false);

  // Countdown for the resend button.
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = window.setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => window.clearInterval(t);
  }, [cooldown]);

  // PasswordResetRequired hand-off from Login: send the code straight away.
  useEffect(() => {
    if (loading || user) return;
    if (locationState?.autoSend && locationState.email && !autoSent.current) {
      autoSent.current = true;
      void sendCode(locationState.email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user]);

  if (loading) {
    return (
      <div className="auth-page">
        <Loading label="Checking your session…" />
      </div>
    );
  }
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  async function sendCode(address: string): Promise<void> {
    setError(null);
    setInfo(null);
    setSubmitting(true);
    try {
      const username = await requestPasswordReset(address);
      setResolvedUsername(username);
      setPhase('confirm');
      setInfo(`Code sent to ${normalizeEmail(address)}.`);
      setCooldown(RESEND_COOLDOWN_S);
    } catch (err) {
      setError(describeAuthError(err, 'requestReset'));
    } finally {
      setSubmitting(false);
    }
  }

  async function onRequest(e: FormEvent) {
    e.preventDefault();
    await sendCode(email);
  }

  async function onConfirm(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    const cleanCode = normalizeCode(code);
    if (!cleanCode) {
      setError({
        code: 'MissingCode',
        title: 'Enter the code from the email',
        detail: 'It is a 6-digit number. Paste it straight from the email — spaces are fine.',
      });
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setError({
        code: 'Mismatch',
        title: 'Those passwords do not match',
        detail: 'Type the same new password in both boxes — use Show to check.',
      });
      return;
    }
    setSubmitting(true);
    try {
      await confirmPasswordReset(resolvedUsername ?? email, cleanCode, newPassword);
      navigate('/login', {
        replace: true,
        state: {
          notice: 'Password reset — sign in with your new password.',
          email: normalizeEmail(resolvedUsername ?? email),
        },
      });
    } catch (err) {
      setError(describeAuthError(err, 'confirmReset'));
    } finally {
      setSubmitting(false);
    }
  }

  function changeEmail() {
    setPhase('request');
    setResolvedUsername(null);
    setCode('');
    setNewPassword('');
    setConfirmNewPassword('');
    setError(null);
    setInfo(null);
  }

  const shownEmail = normalizeEmail(resolvedUsername ?? email);

  return (
    <div className="auth-page">
      <Card className="auth-card">
        <h1 className="auth-title">Reset password</h1>
        <p className="auth-subtitle">
          {phase === 'request'
            ? 'We email you a code, you pick a new password. About a minute.'
            : 'Step 2 of 2 — enter the code and choose a new password.'}
        </p>

        {locationState?.reason === 'resetRequired' && (
          <AuthAlert
            tone="info"
            title="The DM reset your password"
            detail="Your old password no longer works. Enter the code we are emailing you and choose a new one, then sign in."
          />
        )}

        {phase === 'request' ? (
          <form onSubmit={onRequest} className="auth-form" style={{ marginTop: 'var(--sp-4)' }}>
            <Field
              label="Email"
              required
              hint="The address the DM invited you with. Not sure? Ask the DM — do not create a new account."
            >
              <TextInput
                type="email"
                name="email"
                autoComplete="username"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Field>
            {error && (
              <AuthErrorAlert info={error} email={shownEmail} onRetry={() => setError(null)} />
            )}
            <Button type="submit" size="lg" block loading={submitting}>
              Email me a code
            </Button>
          </form>
        ) : (
          <form onSubmit={onConfirm} className="auth-form" style={{ marginTop: 'var(--sp-4)' }}>
            <p className="auth-lead">
              We emailed a 6-digit code to <strong>{shownEmail}</strong>. It comes from{' '}
              <strong>no-reply@verificationemail.com</strong> — give it a minute and check spam or
              junk if it has not arrived.
            </p>

            {info && <AuthAlert tone="success" title={info} />}

            <Field label="Verification code" required>
              <TextInput
                className="auth-code"
                type="text"
                name="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9 ]*"
                maxLength={12}
                autoFocus
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
              />
            </Field>

            <div className="auth-resend">
              <span>Didn't get it?</span>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={cooldown > 0 || submitting}
                onClick={() => void sendCode(resolvedUsername ?? email)}
              >
                {cooldown > 0 ? `Send again in ${cooldown}s` : 'Send a new code'}
              </Button>
              <Button type="button" size="sm" variant="subtle" onClick={changeEmail}>
                Use a different email
              </Button>
            </div>

            <Field
              label="New password"
              required
              hint="At least 8 characters. The pool may also require upper and lower case, a number and a symbol — we will tell you exactly what is missing."
            >
              <PasswordInput
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
              />
            </Field>
            <Field label="Confirm new password" required>
              <PasswordInput
                autoComplete="new-password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
                minLength={8}
              />
            </Field>

            {error && (
              <AuthErrorAlert
                info={error}
                email={shownEmail}
                onResendCode={() => void sendCode(resolvedUsername ?? email)}
                onRetry={() => setError(null)}
              />
            )}

            <Button type="submit" size="lg" block loading={submitting}>
              Set new password
            </Button>
          </form>
        )}

        <div className="auth-links">
          <Link to="/login" state={{ email: shownEmail }} className="auth-link">
            Back to sign in
          </Link>
        </div>

        <p className="auth-help">
          <strong>Never signed in before?</strong> A reset code cannot be sent until your first
          sign-in is done. Use the temporary password from the DM's invite email instead — and if
          that has expired, the DM can resend it or hand you a new temporary password.
        </p>
      </Card>
    </div>
  );
}
